import { useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { ApiError } from "@/lib/api";

export function QualityRmaLinkIdModal({
  open,
  title,
  idLabel,
  submitLabel,
  onClose,
  onLink,
}: {
  open: boolean;
  title: string;
  idLabel: string;
  submitLabel: string;
  onClose: () => void;
  onLink: (id: number) => Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!open) return null;

  return createPortal(
    <div {...hubModalOverlayProps(false)} role="dialog" aria-modal="true" aria-labelledby="rma-link-id-title">
      <form
        className="my-4 w-full max-w-md rounded-lg border border-slate-200 bg-white shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          const id = Number(draft);
          if (!Number.isFinite(id) || id < 1) {
            setErrorMessage("Enter a numeric id.");
            return;
          }
          setPending(true);
          setErrorMessage(null);
          void onLink(id)
            .then(() => {
              setDraft("");
              onClose();
            })
            .catch((error: unknown) => {
              setErrorMessage(error instanceof ApiError ? error.message : "Could not link that record.");
            })
            .finally(() => setPending(false));
        }}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <h2 id="rma-link-id-title" className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
        </div>
        <div className="space-y-3 px-4 py-4 sm:px-5">
          <div>
            <Label htmlFor="rma-link-id">{idLabel}</Label>
            <Input
              id="rma-link-id"
              value={draft}
              autoFocus
              onChange={(event) => setDraft(event.target.value)}
            />
          </div>
          {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? "Linking…" : submitLabel}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
